import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { fetchStats, logout } from "../api/client";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";
import { format, subDays } from "date-fns";
import {
  Users, Activity, Radio, TrendingUp, DollarSign, Award, CheckCircle2,
  ArrowUpRight, Shield, ArrowRight, Calendar, ChevronDown,
  User, Moon, Sun, MessageSquare, LogOut, PhoneCall, HelpCircle
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

// ── SAFE DATE FORMAT HELPER TO PREVENT RangeError: Invalid time value CRASHES ──
const safeFormatDate = (dateStr, formatPattern = "dd MMM HH:mm:ss") => {
  try {
    if (!dateStr) return "—";

    // Normalize fractional seconds (keep only first 3 digits of ms) to prevent ISO string parsing errors on some browsers
    let normalized = dateStr;
    const dotIdx = normalized.indexOf(".");
    if (dotIdx !== -1) {
      const parts = normalized.split(".");
      if (parts[1] && parts[1].length > 3) {
        let msPart = parts[1];
        let suffix = "";
        const zIdx = msPart.search(/[Z\+\-]/);
        if (zIdx !== -1) {
          suffix = msPart.substring(zIdx);
          msPart = msPart.substring(0, zIdx);
        }
        normalized = parts[0] + "." + msPart.substring(0, 3) + suffix;
      }
    }
    const dateObj = new Date(normalized);
    if (isNaN(dateObj.getTime())) {
      return "—";
    }
    return format(dateObj, formatPattern);
  } catch (err) {
    console.error("Error formatting date:", dateStr, err);
    return "—";
  }
};

// ── REACT ERROR BOUNDARY TO PREVENT BLANK SCREEN CRASHES ──
class DashboardErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("DashboardErrorBoundary caught a rendering error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: "40px",
          margin: "40px auto",
          maxWidth: "600px",
          textAlign: "center",
          background: "rgba(239, 68, 68, 0.05)",
          border: "1px solid rgba(239, 68, 68, 0.2)",
          borderRadius: "16px",
          backdropFilter: "blur(12px)"
        }}>
          <h2 style={{ color: "var(--red)", marginBottom: "16px", fontWeight: "800" }}>Dashboard failed to render</h2>
          <p style={{ color: "var(--text2)", marginBottom: "20px", fontSize: "14px", lineHeight: "1.5" }}>
            A rendering crash occurred. Please reload or contact support with the error details below.
          </p>
          <pre style={{
            background: "rgba(0,0,0,0.4)", padding: "16px", borderRadius: "8px",
            color: "#ef4444", fontFamily: "var(--mono)", fontSize: "12px", overflowX: "auto",
            textAlign: "left", border: "1px solid var(--border)", lineHeight: "1.4"
          }}>
            {this.state.error?.toString() || "Unknown error"}
          </pre>
          <button className="btn btn-primary" style={{ marginTop: "24px" }} onClick={() => window.location.reload()}>
            ↻ Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function DashboardContent() {
  const { user: currentUser, clearAuth } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedLicenseId, setSelectedLicenseId] = useState("");
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem("pine_connector_theme") || "dark");
  const profileMenuRef = useRef(null);

  // Click outside to close profile dropdown menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDropdownLogout = async () => {
    try { await logout(); } catch {}
    clearAuth();
    navigate("/login");
  };

  const handleToggleTheme = () => {
    const nextTheme = currentTheme === "dark" ? "light" : "dark";
    setCurrentTheme(nextTheme);
    localStorage.setItem("pine_connector_theme", nextTheme);
    const ids = ["cyberpunk", "emerald", "sunset", "amethyst", "frost", "rose", "dark", "light"];
    ids.forEach(id => document.body.classList.remove(`theme-${id}`));
    document.body.classList.add(`theme-${nextTheme}`);
  };


  const load = (userId = selectedUserId, licenseId = selectedLicenseId, showSpinner = true) => {
    if (showSpinner) setLoading(true);
    fetchStats(userId, licenseId)
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(selectedUserId, selectedLicenseId, true);
    const t = setInterval(() => {
      load(selectedUserId, selectedLicenseId, false);
    }, 12000);  // Auto-refresh every 12s
    return () => clearInterval(t);
  }, [selectedUserId, selectedLicenseId]);

  if (loading) return <div className="loading">Loading dashboard…</div>;
  if (!stats) return <div className="empty">Could not load stats</div>;

  const isAdmin = stats.user_role === "admin";
  const adminOverview = stats.admin_data?.overview || {};
  const allUsersForFilter = stats.admin_data?.all_users_for_filter || [];

  // Generate beautiful historical dataset for charts (mixes live metrics with baseline trends)
  const generate7DaysData = (stats, isAdmin) => {
    const data = [];
    const baseTotal = stats.total_signals || 0;
    const baseTrades = stats.total_trades || 0;

    const totalSignalsBase = [1200, 1600, 1400, 1800, 1500, 2100, 2400];
    const executedSignalsBase = [982, 1245, 1102, 1412, 1198, 1654, 1823];
    const plBase = [1250, 1850, 1520, 2410, 1980, 2640, 2451];

    for (let i = 6; i >= 0; i--) {
      const d = subDays(new Date(), i);
      const dateStr = format(d, "dd MMM");

      if (isAdmin) {
        const factor = 1 + (Math.random() - 0.5) * 0.25;
        const total = (baseTotal > 15) ? Math.round((baseTotal / 7) * factor) : totalSignalsBase[6 - i];
        const executed = (baseTrades > 15) ? Math.round((baseTrades / 7) * factor) : executedSignalsBase[6 - i];
        data.push({
          date: dateStr,
          "Total Signals": total,
          "Executed Signals": Math.min(total, executed)
        });
      } else {
        const pl = (stats.total_profit && stats.total_profit !== 0)
          ? Math.round(stats.total_profit / 7 * (7 - i) * (1 + (Math.random() - 0.5) * 0.15))
          : plBase[6 - i];
        data.push({
          date: dateStr,
          "P&L (USD)": pl
        });
      }
    }
    return data;
  };

  const chartData = stats.daily_data && stats.daily_data.length > 0
    ? stats.daily_data.map(day => isAdmin ? {
        date: day.date,
        "Total Signals": day.signals,
        "Executed Signals": day.trades
      } : {
        date: day.date,
        "P&L (USD)": day.profit
      })
    : generate7DaysData(stats, isAdmin);

  // Pie chart data
  const donutData = [
    { name: "Executed", value: (stats.status_breakdown?.forwarded || 0) + (stats.status_breakdown?.acknowledged || 0), color: "#3b82f6" },
    { name: "Failed", value: stats.status_breakdown?.error || 0, color: "#ef4444" },
    { name: "Pending", value: stats.status_breakdown?.pending || 0, color: "#f59e0b" },
  ];

  const totalDonutSignals = donutData.reduce((acc, curr) => acc + curr.value, 0);

  // Connection status ping indicator
  const connectionPing = stats.active_accounts > 0 ? "online" : "offline";

  return (
    <div style={{ paddingBottom: "24px" }}>
      {/* ── HEADER ROW ── */}
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">
            {isAdmin
              ? "Welcome back, Admin! Here's what's happening with your platform."
              : `Welcome back, ${currentUser?.name || "Trader"}! Here's your trading overview.`
            }
          </div>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          {/* Timeframe Dynamic Plan Dates */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "rgba(255, 255, 255, 0.02)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius)",
            padding: "8px 16px",
            fontSize: "13px",
            fontWeight: "600",
            color: "var(--text2)"
          }}>
            <Calendar size={14} className="accent" />
            <span>
              {isAdmin 
                ? `${format(subDays(new Date(), 6), "dd MMM yyyy")} - ${format(new Date(), "dd MMM yyyy")}`
                : `${currentUser?.service_start ? safeFormatDate(currentUser.service_start, "dd MMM yyyy") : "N/A"} - ${currentUser?.service_end ? safeFormatDate(currentUser.service_end, "dd MMM yyyy") : "N/A"}`
              }
            </span>
          </div>

          {/* Plan Name Badge (Normal User only) */}
          {!isAdmin && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              padding: "8px 16px",
              fontSize: "12px",
              fontWeight: "700"
            }}>
              <span style={{ color: "var(--accent)", textTransform: "uppercase" }}>
                {currentUser?.service_plan || "DEMO"} PLAN
              </span>
            </div>
          )}

          {/* Profile Dropdown Container */}
          <div ref={profileMenuRef} style={{ position: "relative", zIndex: 1001 }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              background: "rgba(255, 255, 255, 0.02)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius)",
              padding: "8px 16px",
              cursor: "pointer",
              userSelect: "none"
            }} onClick={() => setShowProfileMenu(!showProfileMenu)}>
              <div style={{
                width: "24px", height: "24px", borderRadius: "50%",
                background: "linear-gradient(135deg, var(--accent), var(--accent-purple))",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "11px", fontWeight: "800", color: "#040612"
              }}>
                {isAdmin ? "A" : (currentUser?.name?.charAt(0) || "U").toUpperCase()}
              </div>
              <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--text)" }}>
                {isAdmin ? "Administrator" : currentUser?.name}
              </span>
              <ChevronDown size={14} color="var(--text2)" style={{ transform: showProfileMenu ? "rotate(180deg)" : "none", transition: "transform 0.2s" }} />
            </div>

            {showProfileMenu && (
              <div className="profile-dropdown-menu">
                <div className="dropdown-user-header">
                  <div className="dropdown-avatar">
                    {isAdmin ? "A" : (currentUser?.name?.charAt(0) || "U").toUpperCase()}
                  </div>
                  <div className="dropdown-details">
                    <span className="dropdown-name">{isAdmin ? "Administrator" : currentUser?.name}</span>
                    <span className="dropdown-email">{currentUser?.email}</span>
                  </div>
                </div>

                <div className="dropdown-divider"></div>

                <div className="dropdown-item" onClick={() => { setShowProfileMenu(false); navigate("/profile"); }}>
                  <User size={16} />
                  <span>My Profile</span>
                </div>

                <div className="dropdown-item" onClick={handleToggleTheme}>
                  {currentTheme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                  <span>{currentTheme === "dark" ? "Light Mode" : "Dark Mode"}</span>
                </div>

                <div className="dropdown-item" onClick={() => { setShowProfileMenu(false); navigate("/support"); }}>
                  <HelpCircle size={16} />
                  <span>Support Tiers</span>
                </div>

                <div className="dropdown-item" onClick={() => { 
                  setShowProfileMenu(false); 
                  window.dispatchEvent(new CustomEvent("open-pinebot")); 
                }}>
                  <MessageSquare size={16} />
                  <span>PineBot Assistant</span>
                </div>

                <div className="dropdown-divider"></div>

                <div className="dropdown-item logout" onClick={handleDropdownLogout}>
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </div>
              </div>
            )}
          </div>
        </div>
    </div>

      {/* ── KPI METRICS CARDS GRID ── */ }
  {
    isAdmin ? (
      <div className="stat-grid">
        {/* KPI 1: Total Users */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Users</span>
            <div className="kpi-icon-wrap"><Users size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className="kpi-value">{(adminOverview.total_users ?? 0).toLocaleString()}</span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 12.5%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>

        {/* KPI 2: Active Connections */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Active Connections</span>
            <div className="kpi-icon-wrap"><Activity size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className="kpi-value">{(stats.active_accounts ?? 0).toLocaleString()}</span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 8.3%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>

        {/* KPI 3: Total Signals */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Signals</span>
            <div className="kpi-icon-wrap"><Radio size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className="kpi-value">{(stats.total_signals ?? 0).toLocaleString()}</span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 15.7%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>

        {/* KPI 4: Total Trades */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Trades</span>
            <div className="kpi-icon-wrap"><TrendingUp size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className="kpi-value">{(stats.total_trades ?? 0).toLocaleString()}</span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 10.2%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>

        {/* KPI 5: Total Revenue */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Revenue</span>
            <div className="kpi-icon-wrap"><DollarSign size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className="kpi-value" style={{ color: "var(--accent)", textShadow: "0 0 15px rgba(0, 242, 254, 0.2)" }}>
              ${(stats.total_profit ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 18.0%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>
      </div>
    ) : (
      <div className="stat-grid">
        {/* KPI 1: Active Connection */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Active Connection</span>
            <div className="kpi-icon-wrap" style={{
              color: stats.active_accounts > 0 ? "var(--green)" : "var(--red)",
              background: stats.active_accounts > 0 ? "rgba(16, 185, 129, 0.08)" : "rgba(239, 68, 68, 0.08)"
            }}>
              <Shield size={16} />
            </div>
          </div>
          <div className="kpi-value-row" style={{ marginTop: "6px" }}>
            <span style={{
              fontSize: "18px", fontWeight: "800", color: stats.active_accounts > 0 ? "var(--green)" : "var(--red)",
              display: "flex", alignItems: "center", gap: "8px"
            }}>
              <span style={{
                width: "8px", height: "8px", borderRadius: "50%",
                background: stats.active_accounts > 0 ? "var(--green)" : "var(--red)",
                boxShadow: stats.active_accounts > 0 ? "0 0 8px var(--green)" : "none"
              }} />
              {stats.active_accounts > 0 ? "Connected" : "Disconnected"}
            </span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "12px", display: "flex", justifyContent: "space-between" }}>
            <span>Exness Account</span>
            <span style={{ color: stats.active_accounts > 0 ? "var(--green)" : "var(--red)", fontWeight: "600" }}>{connectionPing}</span>
          </div>
        </div>

        {/* KPI 2: Total Signals */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Total Signals</span>
            <div className="kpi-icon-wrap"><Radio size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className="kpi-value">{(stats.total_signals ?? 0).toLocaleString()}</span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 12.5%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>

        {/* KPI 3: Executed Signals */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Executed Signals</span>
            <div className="kpi-icon-wrap"><CheckCircle2 size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className="kpi-value">{(stats.total_trades ?? 0).toLocaleString()}</span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 10.3%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>

        {/* KPI 4: Win Rate */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Win Rate</span>
            <div className="kpi-icon-wrap"><Award size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className="kpi-value" style={{ color: "var(--green)" }}>{(stats.win_rate ?? 0.0).toFixed(1)}%</span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 5.2%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>

        {/* KPI 5: Total P&L */}
        <div className="kpi-card">
          <div className="kpi-card-header">
            <span className="kpi-label">Total P&L</span>
            <div className="kpi-icon-wrap"><DollarSign size={16} /></div>
          </div>
          <div className="kpi-value-row">
            <span className={`kpi-value ${(stats.total_profit ?? 0) >= 0 ? "green" : "red"}`}>
              {stats.total_profit >= 0 ? `+$${(stats.total_profit ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `-$${Math.abs(stats.total_profit ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </span>
            <span className="kpi-trend up"><ArrowUpRight size={12} /> 18.0%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "8px" }}>vs last 7 days</div>
        </div>
      </div>
    )
  }

  {/* ── ADMIN FILTERS PANEL (IF ADMIN) ── */ }
  {
    isAdmin && (
      <div className="card" style={{ marginBottom: "28px", padding: "20px" }}>
        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", alignItems: "center" }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: "240px", flex: "1" }}>
            <label className="form-label" style={{ color: "var(--accent)" }}>Filter by User</label>
            <select
              className="form-input"
              style={{ background: "rgba(0,0,0,0.2)", borderColor: "var(--border)" }}
              value={selectedUserId}
              onChange={(e) => {
                setSelectedUserId(e.target.value);
                setSelectedLicenseId("");
              }}
            >
              <option value="">All Users</option>
              {allUsersForFilter.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || "—"} ({u.license_ids?.length || 0} Lic)
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0, minWidth: "240px", flex: "1" }}>
            <label className="form-label" style={{ color: "var(--accent)" }}>Filter by License ID</label>
            <select
              className="form-input"
              style={{ background: "rgba(0,0,0,0.2)", borderColor: "var(--border)" }}
              value={selectedLicenseId}
              onChange={(e) => setSelectedLicenseId(e.target.value)}
            >
              <option value="">All Licenses</option>
              {selectedUserId
                ? allUsersForFilter
                  .find((u) => u.id === selectedUserId)
                  ?.license_ids?.map((lic) => (
                    <option key={lic} value={lic}>{lic}</option>
                  ))
                : Array.from(new Set(allUsersForFilter.flatMap((u) => u.license_ids || []))).map((lic) => (
                  <option key={lic} value={lic}>{lic}</option>
                ))}
            </select>
          </div>

          {(selectedUserId || selectedLicenseId) && (
            <button
              className="btn btn-ghost"
              style={{ marginTop: "24px", height: "42px" }}
              onClick={() => {
                setSelectedUserId("");
                setSelectedLicenseId("");
              }}
            >
              ✕ Clear Filters
            </button>
          )}
        </div>
      </div>
    )
  }

  {/* ── CHARTS CONTAINER SECTION ── */ }
  <div className="grid-2" style={{ marginBottom: "28px" }}>
    {/* LINE CHART CARD */}
    <div className="card">
      <div className="card-header">
        <span>{isAdmin ? "Signals Overview" : "Performance Overview"}</span>
        {!isAdmin && (
          <div style={{
            display: "flex", alignItems: "center", gap: "6px",
            background: "rgba(255, 255, 255, 0.02)", border: "1px solid var(--border)",
            borderRadius: "12px", padding: "4px 10px", fontSize: "11px", fontWeight: "700", color: "var(--text2)"
          }}>
            <span>Last 7 Days</span>
            <ChevronDown size={12} />
          </div>
        )}
      </div>
      <div className="chart-wrap" style={{ height: "300px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorExecuted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis dataKey="date" stroke="var(--text2)" fontSize={11} tickLine={false} />
            <YAxis stroke="var(--text2)" fontSize={11} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                background: "var(--modal-bg)",
                border: "1px solid var(--border)",
                borderRadius: "12px",
                boxShadow: "var(--shadow)",
                backdropFilter: "blur(12px)",
                color: "var(--text)"
              }}
              itemStyle={{ fontSize: "12.5px", color: "var(--text)" }}
              labelStyle={{ fontWeight: "700", marginBottom: "4px", fontSize: "11px", color: "var(--accent)" }}
            />
            {isAdmin ? (
              <>
                <Area type="monotone" dataKey="Total Signals" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorTotal)" />
                <Area type="monotone" dataKey="Executed Signals" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorExecuted)" />
              </>
            ) : (
              <Area type="monotone" dataKey="P&L (USD)" stroke="#3b82f6" strokeWidth={2.5} fillOpacity={1} fill="url(#colorTotal)" />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>

    {/* DONUT CHART CARD */}
    <div className="card">
      <div className="card-header">
        <span>Signals by Status</span>
      </div>
      <div className="chart-wrap" style={{ height: "300px", display: "flex", alignItems: "center" }}>
        <div style={{ position: "relative", width: "160px", height: "160px", flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={donutData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={75}
                paddingAngle={3}
                dataKey="value"
              >
                {donutData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} stroke="var(--bg-card)" strokeWidth={2} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          {/* ABSOLUTE CENTER LABEL */}
          <div style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none"
          }}>
            <div style={{ fontSize: "20px", fontWeight: "800", color: "var(--text)", fontFamily: "var(--mono)", letterSpacing: "-0.5px" }}>
              {totalDonutSignals.toLocaleString()}
            </div>
            <div style={{ fontSize: "9px", textTransform: "uppercase", color: "var(--text2)", letterSpacing: "1px", fontWeight: "700", marginTop: "2px" }}>
              Total
            </div>
          </div>
        </div>

        {/* CUSTOM DONUT LEGEND */}
        <div style={{ marginLeft: "40px", flex: 1, display: "flex", flexDirection: "column", gap: "16px" }}>
          {donutData.map((item, idx) => {
            const percentage = totalDonutSignals > 0 ? ((item.value / totalDonutSignals) * 100).toFixed(1) : "0.0";
            return (
              <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: item.color }} />
                  <span style={{ fontSize: "13.5px", fontWeight: "600", color: "var(--text)" }}>{item.name}</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                  <span style={{ fontSize: "13.5px", fontWeight: "700", color: "var(--text)", fontFamily: "var(--mono)" }}>
                    {(item.value || 0).toLocaleString()}
                  </span>
                  <span style={{ fontSize: "11px", color: "var(--text2)" }}>({percentage}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </div>

  {/* ── TABLES & LISTS GRID SECTION ── */ }
  {
    isAdmin ? (
      <>
        <div className="grid-2" style={{ marginBottom: "28px" }}>
          {/* ADMIN TABLE 1: RECENT SIGNALS */}
          <div className="card">
            <div className="card-header">
              <span>Recent Signals</span>
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: "11.5px", borderRadius: "8px" }}>View All</button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Symbol</th>
                    <th>Action</th>
                    <th>Price</th>
                    <th>Status</th>
                    <th>User</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.recent_signals || []).slice(0, 5).map((s) => (
                    <tr key={s.id}>
                      <td style={{ color: "var(--text2)", fontSize: "12px" }}>
                        {safeFormatDate(s.received_at, "dd MMM HH:mm:ss")}
                      </td>
                      <td style={{ fontWeight: "700", color: "#fff" }}>{s.symbol}</td>
                      <td>
                        <span className={`badge badge-${(s.action || "").toLowerCase().startsWith("buy") ? "buy" : "sell"}`}>
                          {s.action}
                        </span>
                      </td>
                      <td className="mono" style={{ fontWeight: "600" }}>{s.price ?? "—"}</td>
                      <td>
                        <span className={`badge ${s.status === "forwarded" || s.status === "acknowledged" ? "badge-ok" : s.status === "error" ? "badge-error" : "badge-pending"
                          }`}>
                          {s.status}
                        </span>
                      </td>
                      <td style={{ fontWeight: "500" }}>{s.user_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ADMIN TABLE 2: TOP ACTIVE USERS */}
          <div className="card">
            <div className="card-header">
              <span>Top Active Users</span>
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: "11.5px", borderRadius: "8px" }}>View All</button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>User</th>
                    <th style={{ textAlign: "right" }}>Signals</th>
                    <th style={{ textAlign: "right" }}>Trades</th>
                    <th style={{ textAlign: "right" }}>Win Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.admin_data?.top_users_by_trades || []).map((user) => {
                    return (
                      <tr key={user.user_id}>
                        <td style={{ fontWeight: "600", color: "#fff" }}>{user.name || "—"}</td>
                        <td className="mono" style={{ textAlign: "right", color: "var(--text2)" }}>
                          {(user.signals_received ?? 0).toLocaleString()}
                        </td>
                        <td className="mono" style={{ textAlign: "right" }}>
                          {(user.trades_copied ?? 0).toLocaleString()}
                        </td>
                        <td className="mono" style={{ textAlign: "right", fontWeight: "700", color: "var(--green)" }}>
                          {(user.win_rate ?? 0.0).toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ADMIN BOTTOM 4 WIDGETS */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "20px" }}>
          {/* Widget 1: Connections limit */}
          <div className="card" style={{ padding: "20px", marginBottom: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", alignItems: "center" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Connections</span>
              <button className="btn btn-ghost" style={{ padding: "2px 8px", fontSize: "10px", borderRadius: "6px" }}>View All</button>
            </div>
            <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
              <span style={{ fontSize: "24px", fontWeight: "800", color: "#fff", fontFamily: "var(--mono)" }}>
                {stats.active_accounts !== undefined ? stats.active_accounts : 0}
              </span>
              <span style={{ fontSize: "13px", color: "var(--text2)" }}>/ 1,200</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11.5px", color: "var(--text2)", marginTop: "8px", marginBottom: "6px" }}>
              <span>Active / Total</span>
              <span style={{ color: "var(--green)" }}>
                {stats.active_accounts !== undefined ? ((stats.active_accounts / 1200) * 100).toFixed(1) + "%" : "0.0%"}
              </span>
            </div>
            <div style={{ width: "100%", height: "4px", borderRadius: "4px", background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
              <div style={{
                width: stats.active_accounts !== undefined ? `${(stats.active_accounts / 1200) * 100}%` : "0%",
                height: "100%", background: "var(--green)", borderRadius: "4px"
              }} />
            </div>
          </div>

          {/* Widget 2: Trading Accounts */}
          <div className="card" style={{ padding: "20px", marginBottom: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", alignItems: "center" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Trading Accounts</span>
              <button className="btn btn-ghost" style={{ padding: "2px 8px", fontSize: "10px", borderRadius: "6px" }}>View All</button>
            </div>
            <div style={{ fontSize: "24px", fontWeight: "800", color: "#fff", fontFamily: "var(--mono)" }}>
              {stats.active_accounts || 0}
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "12px" }}>
              Connected Accounts
            </div>
          </div>

          {/* Widget 3: Success Rate */}
          <div className="card" style={{ padding: "20px", marginBottom: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", alignItems: "center" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Success Rate</span>
              <button className="btn btn-ghost" style={{ padding: "2px 8px", fontSize: "10px", borderRadius: "6px" }}>View All</button>
            </div>
            <div style={{ fontSize: "24px", fontWeight: "800", color: "var(--green)", fontFamily: "var(--mono)", textShadow: "0 0 10px rgba(16,185,129,0.2)" }}>
              {(stats.win_rate ?? 0.0).toFixed(1)}%
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "12px" }}>
              Overall Success Rate
            </div>
          </div>

          {/* Widget 4: Tickets Raised */}
          <div className="card" style={{ padding: "20px", marginBottom: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", alignItems: "center" }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Tickets Raised</span>
              <button className="btn btn-ghost" style={{ padding: "2px 8px", fontSize: "10px", borderRadius: "6px" }} onClick={() => navigate("/users", { state: { activeTab: "renewals" } })}>View All</button>
            </div>
            <div style={{ fontSize: "24px", fontWeight: "800", color: "#fff", fontFamily: "var(--mono)" }}>
              {adminOverview.pending_renewals ?? 0}
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--text2)", marginTop: "12px" }}>
              Pending Tickets
            </div>
          </div>
        </div>
      </>
    ) : (
      <div style={{ display: "grid", gridTemplateColumns: "2.1fr 0.9fr", gap: "20px" }}>
        {/* USER TABLES SECTION */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* USER TABLE 1: RECENT SIGNALS */}
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header">
              <span>Recent Signals</span>
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: "11.5px", borderRadius: "8px" }} onClick={() => navigate("/signals")}>View All</button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Symbol</th>
                    <th>Action</th>
                    <th>Price</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.recent_signals || []).slice(0, 4).map((s) => {
                    return (
                      <tr key={s.id}>
                        <td style={{ color: "var(--text2)", fontSize: "12px" }}>
                          {safeFormatDate(s.received_at, "dd MMM HH:mm:ss")}
                        </td>
                        <td style={{ fontWeight: "700", color: "#fff" }}>{s.symbol}</td>
                        <td>
                          <span className={`badge badge-${(s.action || "").toLowerCase().startsWith("buy") ? "buy" : "sell"}`}>
                            {s.action}
                          </span>
                        </td>
                        <td className="mono" style={{ fontWeight: "600" }}>{s.price ?? "—"}</td>
                        <td>
                          <span className={`badge ${s.status === "forwarded" || s.status === "acknowledged" ? "badge-ok" : s.status === "error" ? "badge-error" : "badge-pending"
                            }`}>
                            {s.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* USER TABLE 2: RECENT TRADES */}
          <div className="card" style={{ marginBottom: 0 }}>
            <div className="card-header">
              <span>Recent Trades</span>
              <button className="btn btn-ghost" style={{ padding: "4px 10px", fontSize: "11.5px", borderRadius: "8px" }} onClick={() => navigate("/trade-history")}>View All</button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Type</th>
                    <th>Lot</th>
                    <th>Open Price</th>
                    <th style={{ textAlign: "right" }}>P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {(stats.recent_trades || []).slice(0, 4).map((t) => {
                    const plVal = t.profit ?? 0;
                    const plText = plVal >= 0 ? `+$${plVal.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : `-$${Math.abs(plVal).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
                    return (
                      <tr key={`trade-${t.id}`}>
                        <td style={{ fontWeight: "700", color: "#fff" }}>{t.symbol}</td>
                        <td>
                          <span className={`badge badge-${(t.action || "").toLowerCase().startsWith("buy") ? "buy" : "sell"}`}>
                            {t.action}
                          </span>
                        </td>
                        <td className="mono">{t.lot ?? 0.01}</td>
                        <td className="mono" style={{ fontWeight: "600" }}>{t.open_price ?? "—"}</td>
                        <td className={`mono ${plVal >= 0 ? "green" : "red"}`} style={{ textAlign: "right", fontWeight: "700" }}>
                          {plText}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* USER RIGHT PROFILE & SUBSCRIPTION WIDGETS */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Card 1: Subscription details */}
          <div className="card" style={{ padding: "20px", display: "flex", flexDirection: "column", justifyContent: "space-between", height: "185px", marginBottom: 0 }}>
            <div>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "var(--text2)", textTransform: "uppercase", letterSpacing: "1px" }}>Subscription</span>
              <div style={{ fontSize: "20px", fontWeight: "800", color: "#fff", marginTop: "8px" }}>
                {currentUser?.service_plan ? currentUser.service_plan.charAt(0).toUpperCase() + currentUser.service_plan.slice(1) : "Demo"} Plan
              </div>
              <div style={{ fontSize: "11px", color: "var(--text2)", marginTop: "2px", fontFamily: "var(--mono)" }}>
                Valid Until: {currentUser?.service_end ? safeFormatDate(currentUser.service_end, "dd MMM yyyy") : "N/A"}
              </div>
            </div>
            <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/profile")}>
              <span>View Plan</span>
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    )
  }
    </div >
  );
}

export default function Dashboard() {
  return (
    <DashboardErrorBoundary>
      <DashboardContent />
    </DashboardErrorBoundary>
  );
}
